import { html, internalProperty, property } from 'lit-element';
import { nothing } from 'lit-html';
import { classMap } from 'lit-html/directives/class-map.js';
import {
  Keys,
  KatLitElement,
  register,
  event,
  EventEmitter,
} from '../../shared/base';
import { strings } from './strings';

const MAX_FEEDBACK_TEXT_LENGTH = 500;
const EMOJI_STRING_MAP = {
  1: 'very_unsatisfied_option',
  2: 'unsatisfied_option',
  3: 'neutral_option',
  4: 'satisfied_option',
  5: 'very_satisfied_option',
};

const RATING_SCORE = [1, 2, 3, 4, 5];

/**
 * @component {kat-hmd} KatalHmd How’s My Driving (HMD) is a pop-up user satisfaction survey built on the modal component. It may open in a modal or be triggered by the "Feedback" tab in the lower-left corner.
 * @guideline Do Use to collect customer opinions on experiences or services.
 * @guideline Do Listen to the submit event to handle HMD submissions. Use the error attribute to communicate submission errors to the user.
 * @guideline Dont Modify question prompts or HTML inside of the component.
 * @guideline Dont Listen to the cancel button for click events. This button has been pre-wired to close the component.
 * @example Sample {"visible": false, "locale":"en-US", "rating":"0", "comment":"", "error":""}
 * @example Localized {"visible": false, "locale": "ja-JP", "rating":"0", "comment":"", "error":""}
 * @example Error {"visible": false, "locale":"en-US", "error": "Failed to submit your feedback. Please try again.", "rating":"3", "comment":"Your website is average."}
 * @example WithStarRating {"visible": false, "locale": "en-US", "rating":"0", "comment":"", "error":"", "rating-style":"stars"}
 * @example WithNumericalRating {"visible": false, "locale": "en-US", "rating":"0", "comment":"", "error":"", "rating-style":"numerical"}
 * @status Production
 */
@register('kat-hmd')
export class KatHmd extends KatLitElement {
  /** Determines if the HMD is visible to the user */
  @property()
  visible = false;

  /** Error text to display */
  @property()
  error = '';

  /** Warning text to display */
  @property()
  warning?: string;

  /** Feedback provided by the user */
  @property()
  comment = '';

  /**
   * The title of the form. Defaults to "Rate This Page"
   * @required false
   */
  @property()
  title: string;

  /**
   * Numeric HMD sentiment rating (0-5), defaults to 0 for unselected
   * @type {string | number}
   */
  @property()
  rating = 0;

  /** Locale for the prompt text */
  @property()
  locale = 'en-US';

  /**
   * Show success message instead of form
   * @enum {value} normal Shows the HMD form
   * @enum {value} busy Shows the HMD form, covered by a loading spinner to show that submission is in progress
   * @enum {value} success Shows a thank you message.
   */
  @property()
  state = '';

  /** Subject provided by the user */
  @property()
  subject = '';

  /**
   * The mode of the HMD - Default is modal
   * @enum {value} modal HMD will load as a modal - Default
   * @enum {value} inline HMD will load as an inline form
   */
  @property()
  mode = 'modal';

  /**
   * The style of the HMD rating- Default is emoji
   * @enum {value} emoji rating use emoji to rate - Default
   * @enum {value} stars rating use stars to rate
   * @enum {value} numerical rating use numbers to rate
   */
  @property({ attribute: 'rating-style' })
  ratingStyle = 'emoji';

  // private property to handle event.detail.value + comment text
  @internalProperty()
  private _remaining: number = MAX_FEEDBACK_TEXT_LENGTH;

  /** Fires when the user submits the HMD. */
  @event('submit')
  private _submit: EventEmitter<{
    rating: number;
    comment: string;
    subject: string;
  }>;

  /** Fires when the HMD is cancelled or otherwise hidden. */
  @event('close', true)
  private _close: EventEmitter<{ visible: boolean }>;

  /** Fires when the HMD modal dialog opens. */
  @event('open', true)
  private _open: EventEmitter<{ visible: boolean }>;

  // disable shadow-dom
  createRenderRoot() {
    return this;
  }

  updated(changedProperties) {
    if (changedProperties.has('comment')) {
      this.handleRemaining(this.comment);
    }
  }

  handleRemaining(value) {
    this._remaining = MAX_FEEDBACK_TEXT_LENGTH - value.length;
  }

  async close() {
    this.visible = false;

    await this.updateComplete;
    this._close.emit({ visible: this.visible });
  }

  async open() {
    this.visible = true;

    await this.updateComplete;
    this._open.emit({ visible: this.visible });
  }

  reset() {
    this.comment = '';
    this.rating = 0;
    this.subject = '';
    this.state = 'normal';
    this.error = '';
  }

  succeed() {
    this.state = 'success';
  }

  render() {
    const renderContent = this.getContent();
    return html`
      ${this.mode === 'modal'
        ? html`
            <kat-modal
              @open=${this.open}
              @close=${this.close}
              ?visible=${this.visible}
              class="kat-hmd-container"
            >
              ${renderContent}
            </kat-modal>
          `
        : html`<div class="kat-hmd-container">${renderContent}</div>`}
    `;
  }

  getContent() {
    const rating = this.rating;
    const classes = {
      'kat-hmd-footer': true,
      'kat-hmd-show': this.state === 'normal',
      'kat-hmd-hide': this.state === 'success' || this.state === 'busy',
    };
    const cancelBtn = html`
      <kat-button
        class="katHmdCancelBtn"
        variant="secondary"
        label=${this.getString('cancel_label')}
        @click=${this.cancelBtnClick}
      ></kat-button>
    `;
    const submitBtn = html`
      <kat-button
        class="katHmdSubmitBtn"
        label=${this.getString('submit_label')}
        ?disabled=${rating === 0}
        @click=${this.submitBtnClick}
      ></kat-button>
    `;

    return html`
      <div slot="title" class="kat-hmd-title">
        ${this.title ? this.title : this.getString('modal_title')}
      </div>

      <div class="kat-hmd-body kat-spacing-group">
        ${this.getKatModalBody(this.state)}
      </div>

      <div slot="footer" class=${classMap(classes)}>
        ${cancelBtn} ${submitBtn}
      </div>
    `;
  }

  getKatModalBody(state) {
    const { error, rating } = this;

    if (state === 'success') {
      return html`<p class="khmdSuccess">${this.getString('thanks')}</p>`;
    }
    if (state === 'busy') {
      return html`
        <div class="kat-hmd-spinner-container">
          <kat-spinner></kat-spinner>
        </div>
      `;
    }
    if (state !== 'success' && this.state !== 'busy') {
      const getLabel = html`
        <kat-label class="kat-satisfaction-rating-label kat-hmd-label-spacing"
          >${this.getString('rating_prompt')}</kat-label
        >
      `;
      const getText = html`
        <div class="kat-text-right kat-charcount">
          ${this._remaining} ${this.getString('chars_remaining')}
        </div>
      `;
      const getAlert = error.length
        ? html`
            <kat-alert variant="danger" description="${error}"></kat-alert>
          `
        : this.warning?.length
        ? html`
            <kat-alert
              variant="warning"
              description="${this.warning}"
            ></kat-alert>
          `
        : nothing;

      return html`
        ${getAlert}
        <div
          class="kat-hmd-satisfaction-rating-field kat-hmd-form-item-spacing"
        >
          ${getLabel} ${this.getSatisfactionRatingOptions(rating)}
        </div>
        <div class="kat-hmd-form-item-spacing">${this.getDropDown()}</div>
        <div class="kat-hmd-form-item-spacing">
          ${this.getTextarea()}
          <div class="kat-hmd-textarea-metadata content kat-row kat-no-gutters">
            <div class="kat-disclaimer">
              <span
                >${this.getString('popup_prompt')}
                <kat-tooltip
                  trigger-icon="info-circle"
                  size="small"
                  label=${this.getString('disclaimer')}
                  position="top"
                ></kat-tooltip>
              </span>
            </div>
            ${getText}
          </div>
        </div>
      `;
    }
  }

  getTextarea() {
    return html`
      <kat-textarea
        state="default"
        maxlength=${MAX_FEEDBACK_TEXT_LENGTH}
        value=${this.comment}
        label=${this.getString('comment_prompt')}
        placeholder=${this.getString('textarea_placeholder')}
        @input=${this.handleTextareaInput}
        @change=${this.handleTextareaChange}
      ></kat-textarea>
    `;
  }

  getDropDown() {
    const subject = this.subject;
    const options = JSON.stringify([
      {
        name: this.getString('subject_suggestion'),
        value: 'suggestion',
      },
      {
        name: this.getString('subject_technical'),
        value: 'issue',
      },
      {
        name: this.getString('subject_compliment'),
        value: 'compliment',
      },
      {
        name: this.getString('subject_other'),
        value: 'other',
      },
    ]);

    return html`
      <kat-dropdown
        class="kat-subject-dropdown"
        label=${this.getString('subject_prompt')}
        options=${options}
        placeholder=${this.getString('subject_select')}
        value=${subject}
        @change=${this.handleDropdownChange}
      ></kat-dropdown>
    `;
  }

  getSatisfactionRatingOptions(rating) {
    if (this.ratingStyle === 'emoji') {
      return this.getEmojiRating(rating);
    }
    if (this.ratingStyle === 'numerical') {
      return this.getNumRating(rating);
    }
    if (this.ratingStyle === 'stars') {
      return this.getStarRating(rating);
    }
  }

  getStarRating(rating) {
    return html`
      <ul class="kat-satisfaction-rating kat-hmd-form-item-spacing">
        <kat-star-rating
          class="hmd-rating-item-icon hmd2-star"
          variant="input"
          name="starrating"
          value=${rating}
          @change=${this.handleStarRatingChange}
        ></kat-star-rating>
      </ul>
    `;
  }

  getEmojiRating(rating) {
    let ratingContent = [];
    ratingContent = RATING_SCORE.map(num => {
      const classes = {
        'hmd-rating-item-icon': true,
        'hmd2-emoji': true,
        // TODO: coerce number properties instead of disabling eqeqeq
        // eslint-disable-next-line eqeqeq
        selected: rating == num,
      };
      const key = 'hmd2-emoji-' + num;
      classes[key] = true;

      return html`
        <li class=${'rating-' + num}>
          <a
            class=${classMap(classes)}
            tabindex="0"
            data-rating=${num}
            aria-label=${this.getString(EMOJI_STRING_MAP[num])}
          ></a>
        </li>
      `;
    });
    return html`
      <ul
        class="kat-satisfaction-rating kat-hmd-form-item-spacing"
        @click=${this.handleRatingChangeEvents}
        @keyup=${this.handleRatingChangeEvents}
      >
        ${ratingContent}
      </ul>
    `;
  }

  getNumRating(rating) {
    let ratingContent = [];
    ratingContent = RATING_SCORE.map(num => {
      const classes = {
        'hmd-rating-item-icon': true,
        'hmd2-number-rating': true,
        'hmd2-number': true,
        // TODO: coerce number properties instead of disabling eqeqeq
        // eslint-disable-next-line eqeqeq
        selected: rating == num,
      };

      return html`
        <li class=${'numrating-' + num}>
          <div
            class=${classMap(classes)}
            tabindex="0"
            data-rating=${num}
            aria-label=${this.getString(EMOJI_STRING_MAP[num])}
          >
            <span class="hmd2-number-rating" aria-hidden="true">${num}</span>
          </div>
          ${num === 1 || num === 3 || num === 5
            ? html`
                <p class="hmd2-number-text" aria-hidden="true">
                  ${this.getString(EMOJI_STRING_MAP[num])}
                </p>
              `
            : nothing}
        </li>
      `;
    });
    return html`
      <ul
        @click=${this.handleRatingChangeEvents}
        @keyup=${this.handleRatingChangeEvents}
        class="kat-satisfaction-rating kat-form-item-spacing"
      >
        ${ratingContent}
      </ul>
    `;
  }

  cancelBtnClick() {
    this.reset();
    this.close();
  }

  submitBtnClick() {
    this._submit.emit({
      rating: this.rating,
      comment: this.comment,
      subject: this.subject,
    });
  }

  handleTextareaChange(event) {
    this.comment = event.target.value;
  }

  handleTextareaInput(event) {
    this.handleRemaining(event.target.value);
  }

  handleDropdownChange(event) {
    this.subject = event.target.value;
  }

  handleStarRatingChange(event) {
    this.rating = event.target.value;
  }

  handleRatingChangeEvents(event) {
    const ratingTarget = event.target.closest('[data-rating]');
    if (!ratingTarget) return;
    if (event.type === 'keyup' && !Keys.Confirm.includes(event.keyCode)) return;
    this.rating = ratingTarget.dataset.rating;
  }

  getString(stringId) {
    const locale = this.locale;
    let string = strings[stringId][locale];
    if (!string) {
      string = strings[stringId]['en-US'];
    }
    return string;
  }
}
